import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { FaEdit, FaSave, FaTimes, FaTrash } from "react-icons/fa";
import "../../css/others/Table.css";
import "../../css/others/Popup.css";
import Pagination from "../../Reusablecomponents/Pagination";
import { apiCall } from "../../main/ApiCall";
import AdminCheckListDetailsEdit from "../../popups/AdminCheckListDetailsEdit";
import ToastPopup from "../../popups/ToastPopup";
import { createToastMessage, ToastMessage } from "../../interFaces/interfaces";
const AdminCheckListDetails: React.FC = () => {
  const { localTemplateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, isIntegratedChecklist, controls = [] } = location.state || {};
  const [details, setDetails] = useState<any>(null);
  const [, setLoading] = useState<boolean>(true);
  const [newQuestions, setNewQuestions] = useState<any[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editableQuestion, setEditableQuestion] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; id: string | null }>({ isOpen: false, id: null });
  const [showRedirectPopup, setShowRedirectPopup] = useState<boolean>(false);
  const [savingQuestionId, setSavingQuestionId] = useState<number | null>(null);
  const getActualFieldName = (displayName: string): string => {
    const fieldMap: { [key: string]: string } = { "PIMS": "pims", "ISMS": "isms", "BCMS": "bcms", "CMMI": "cmmi", "AIMS": "aims", "SOC 1": "soc1", "SOC 2": "soc2", "QMS": "qms", "ERM": "erm", "ITSM": "itsm", "ERS": "ers", "OHS": "ohs" };
    return fieldMap[displayName] || displayName.toLowerCase().replace(/\s+/g, '');
  };
  useEffect(() => {
    if (status === "ACTIVE") {
      const timer = setTimeout(() => {
        setShowRedirectPopup(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);
  useEffect(() => {
    if (showRedirectPopup) {
      const timer = setTimeout(() => {
        navigate(-1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showRedirectPopup, navigate]);
  const fetchChecklistDetails = useCallback(async () => {
    if (!localTemplateId) return;
    setLoading(true);
    try {
      const checklist = await apiCall("get", `/api/local-templates/${localTemplateId}/questions/edit`, null, { page: currentPage - 1, size: itemsPerPage });
      setDetails(checklist || { content: [] });
      setTotalElements(checklist.totalElements || 0);
      setTotalPages(checklist.totalPages || 0);
    } catch (error) {
      console.error("Error fetching checklist details:", error);
    }
  }, [localTemplateId, currentPage, itemsPerPage]);
  useEffect(() => { fetchChecklistDetails(); }, [fetchChecklistDetails]);
  const handleAddRow = () => {
    if (status === "ACTIVE") return;
    const defaultControlValues: { [key: string]: string } = {};
    controls.forEach((control: string) => {
      const actualFieldName = getActualFieldName(control);
      defaultControlValues[actualFieldName] = "";
    });
    const newRow = { questionId: Date.now(), questionText: "", expectedArtifact: "", practiceArea: "", ...defaultControlValues, isNew: true, isSaving: false };
    setNewQuestions((prev) => [...prev, newRow]);
  };
  const restrictedChars = /[\\:|/]/;
  const containsSpecialChars = (str: string) => {
    return restrictedChars.test(str);
  };
  const handleRestrictedInput = (value: string, updateFn: () => void) => {
    if (containsSpecialChars(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
      return;
    }
    updateFn();
  };
  const handleRemoveRow = () => {
    if (!deleteConfirm.id || !localTemplateId || status === "ACTIVE") return;
    apiCall("delete", `/api/local-templates/${localTemplateId}/questions/${deleteConfirm.id}`, { headers: { "Content-Type": "application/json" } })
      .then(() => {
        fetchChecklistDetails();
        setMessage(createToastMessage("Question deleted successfully!", "success", 3000));
      })
      .catch((error: any) => {
        const errorMessage = error?.response?.data?.message;
        setMessage(createToastMessage(errorMessage, "error", 3000));
      })
      .finally(() => {
        setDeleteConfirm({ isOpen: false, id: null });
      });
  };
  const validateQuestion = (question: any) => {
    if (!question.questionText.trim() || !question.expectedArtifact.trim()) {
      return false;
    }
    return true;
  };
  const handleSaveSingleQuestion = async (index: number) => {
    if (status === "ACTIVE") return;
    const questionToSave = newQuestions[index];
    if (!validateQuestion(questionToSave)) {
      setMessage(createToastMessage("Please fill all required fields (Question and Expected Artifact)", "warning", 3000));
      return;
    }
    setSavingQuestionId(questionToSave.questionId);
    const questionData = { questionText: questionToSave.questionText, expectedArtifact: questionToSave.expectedArtifact, practiceArea: questionToSave.practiceArea };
    const controlFields: { [key: string]: any } = {};
    controls.forEach((control: string) => {
      const actualFieldName = getActualFieldName(control);
      controlFields[actualFieldName] = questionToSave[actualFieldName] || "";
    });
    const finalQuestionData = { ...questionData, ...controlFields };
    try {
      await apiCall("post", `/api/local-templates/${localTemplateId}/questions`, [finalQuestionData]);
      setNewQuestions((prev) => prev.filter((_, i) => i !== index));
      fetchChecklistDetails();
      setMessage(createToastMessage("Question added successfully!", "success", 3000));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage, "error", 3000));
    } finally {
      setSavingQuestionId(null);
    }
  };
  const handleEditClick = (id: string) => {
    if (status === "ACTIVE") return;
    const questionToEdit = details?.content.find((item: any) => item.questionId.toString() === id);
    if (questionToEdit) {
      setEditableQuestion(questionToEdit);
      setIsEditModalOpen(true);
    }
  };
  const handleModalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (containsSpecialChars(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
      return;
    }
    setEditableQuestion((prev: any) => ({ ...prev, [name]: value }));
  };
  const handleSaveChanges = async () => {
    if (!editableQuestion || status === "ACTIVE") return;
    if (!editableQuestion.questionText.trim() || !editableQuestion.expectedArtifact.trim()) {
      setMessage(createToastMessage("Please fill all required fields", "warning", 3000));
      return;
    }
    const updateData: { questionText: string; expectedArtifact: string; practiceArea: string;[key: string]: any; } = { questionText: editableQuestion.questionText, expectedArtifact: editableQuestion.expectedArtifact, practiceArea: editableQuestion.practiceArea };
    controls.forEach((control: string) => {
      const actualFieldName = getActualFieldName(control);
      updateData[actualFieldName] = editableQuestion[actualFieldName] || "";
    });
    try {
      await apiCall("patch", `/api/local-templates/${localTemplateId}/questions/${editableQuestion.questionId}`, updateData);
      setMessage(createToastMessage("Question updated successfully!", "success", 3000));
      setIsEditModalOpen(false);
      fetchChecklistDetails();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  }
  const handleCancelNewQuestion = (index: number) => {
    setNewQuestions((prev) => prev.filter((_, i) => i !== index));
  };
  const renderTableHeaders = () => {
    const controlHeaders = { pims: "PIMS", isms: "ISMS", bcms: "BCMS", cmmi: "CMMI", aims: "AIMS", soc1: "SOC 1", soc2: "SOC 2", qms: "QMS", "erm": "ERM", "itsm": "ITSM", "ers": "ERS", "ohs": "OHS" };
    return (
      <tr>
        <th>S.No.</th>
        <th className="header-width">Question</th>
        <th className="header-width">Expected Artifact</th>
        {!isIntegratedChecklist && <th>Control/Practice Area</th>}
        {controls.map((control: React.Key | null | undefined) => {
          if (typeof control === "string") {
            return <th key={control} className="header-width">{controlHeaders[control as keyof typeof controlHeaders] || control}</th>;
          }
          return <th key={String(control)}>{String(control)}</th>;
        })}
        {status !== "ACTIVE" && <th>Action</th>}
      </tr>
    );
  };
  const renderQuestionRow = (item: any, index: number) => {
    return (
      <tr key={item.questionId}>
        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
        <td>{item.questionText || "Empty"}</td>
        <td>{item.expectedArtifact || "Empty"}</td>
        {!isIntegratedChecklist && <td>{item.practiceArea || "Empty"}</td>}
        {controls.map((control: React.Key | null | undefined) => {
          if (typeof control === "string") {
            const actualFieldName = getActualFieldName(control);
            return <td key={control}>{item[actualFieldName] || "Empty"}</td>;
          }
          return <td key={String(control)}>Empty</td>;
        })}
        {status !== "ACTIVE" && (
          <td>
            <FaEdit className="action-button" onClick={() => handleEditClick(item.questionId.toString())} title="Edit" />
            <FaTrash className="action-button" onClick={() => setDeleteConfirm({ isOpen: true, id: item.questionId.toString() })} title="Delete" />
          </td>
        )}
      </tr>
    );
  };
  const renderNewQuestionRow = (question: any, index: number) => {
    const isSaving = savingQuestionId === question.questionId;
    return (
      <tr key={`new-${question.questionId}`}>
        <td>{details?.content?.length + index + 1}</td>
        <td>
          <textarea value={question.questionText} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updatedQuestions = [...newQuestions]; updatedQuestions[index].questionText = e.target.value; setNewQuestions(updatedQuestions) })} placeholder="Enter question" required disabled={isSaving} />
        </td>
        <td>
          <textarea value={question.expectedArtifact} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updatedQuestions = [...newQuestions]; updatedQuestions[index].expectedArtifact = e.target.value; setNewQuestions(updatedQuestions) })} placeholder="Enter expected artifact" required disabled={isSaving} />
        </td>
        {!isIntegratedChecklist && (
          <td>
            <textarea value={question.practiceArea} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updatedQuestions = [...newQuestions]; updatedQuestions[index].practiceArea = e.target.value; setNewQuestions(updatedQuestions); })} placeholder="Enter Control/Practice Area" required disabled={isSaving} />
          </td>
        )}
        {controls.map((control: React.Key | null | undefined) => {
          if (typeof control === "string") {
            const actualFieldName = getActualFieldName(control);
            return (
              <td key={control}>
                <textarea value={question[actualFieldName] || ""} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updatedQuestions = [...newQuestions]; updatedQuestions[index][actualFieldName] = e.target.value; setNewQuestions(updatedQuestions) })} placeholder={`Enter ${control}`} disabled={isSaving} />
              </td>
            );
          }
          return <td key={String(control)}></td>;
        })}
        <td>
          <FaSave className={`action-button ${isSaving ? 'disabled' : ''}`} onClick={() => handleSaveSingleQuestion(index)} title="Save" />
          <FaTimes className={`action-button ${isSaving ? 'disabled' : ''}`} onClick={() => handleCancelNewQuestion(index)} title="Cancel" />
        </td>
      </tr>
    );
  };
  const calculateColSpan = () => {
    let colSpan = 4;
    if (isIntegratedChecklist) {
      colSpan -= 1;
    }
    colSpan += controls.length;
    if (status !== "ACTIVE") {
      colSpan += 1;
    }
    return colSpan;
  };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Checklist Details</h2>
        {status !== "ACTIVE" && (
          <button className="add-question-button" onClick={handleAddRow}>Add Question</button>
        )}
      </div>
      <div className="table-container">
        <div className="table-inner-container">
          <table className="main-table">
            <thead>
              {renderTableHeaders()}
            </thead>
            <tbody>
              {details?.content?.map((item: any, index: number) => renderQuestionRow(item, index))}
              {newQuestions.map((question, index) => renderNewQuestionRow(question, index))}
              {details?.content?.length === 0 && newQuestions.length === 0 && (
                <tr>
                  <td colSpan={calculateColSpan()} className="no-data"> No questions available for this checklist</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} totalElements={totalElements} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} onItemsPerPageChange={setItemsPerPage} />
      {isEditModalOpen && editableQuestion && (
        <AdminCheckListDetailsEdit question={editableQuestion} onClose={() => setIsEditModalOpen(false)} onSave={handleSaveChanges} onChange={handleModalChange} controls={controls} isIntegratedChecklist={isIntegratedChecklist} />
      )}
      {deleteConfirm.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this question?</p>
            <div className="buttons">
              <button className="cancelbutton" onClick={() => setDeleteConfirm({ isOpen: false, id: null })}>Cancel</button>
              <button className="savebutton" onClick={handleRemoveRow}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {showRedirectPopup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Redirection Notice</h3>
            <p>This checklist is in ACTIVE status. Redirecting to draft version...</p>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default AdminCheckListDetails;